import { ApiError, operationId } from './api.js';
const clone = value => structuredClone(value);
const pk = (scope, id) => JSON.stringify([scope, id]);
const int = (value, min, max) => Number.isSafeInteger(value) && value >= min && value <= max;
/** Client validation is an early error message, not a substitute for server ownership. */
export function validateOperation(op) {
  if (!op || typeof op !== 'object' || Array.isArray(op)) throw new Error('Geçersiz kayıt işlemi.');
  const allowed = ['opId','type','exerciseId','equipmentId','ordinal','reps','weightMinor','setRevision','baseRevision'];
  if (Object.keys(op).some(k => !allowed.includes(k)) || !/^[a-zA-Z0-9_-]{8,128}$/.test(op.opId)) throw new Error('İşlem alanları geçersiz.');
  if (op.type === 'finish') {
    if (!int(op.baseRevision, 0, 1_000_000) || Object.keys(op).some(k => !['opId','type','baseRevision'].includes(k))) throw new Error('Bitirme sürümü geçersiz.');
  } else {
    if (!['set.upsert','set.delete'].includes(op.type) || !/^[a-zA-Z0-9_-]{1,64}$/.test(op.exerciseId) || !/^[a-zA-Z0-9_-]{1,128}$/.test(op.equipmentId) || !int(op.ordinal,1,20) || !int(op.setRevision,0,10000)) throw new Error('Set kimliği/sürümü geçersiz.');
    if (op.type === 'set.upsert' && (!int(op.reps,0,100) || !int(op.weightMinor,0,200000))) throw new Error('Set alanları geçersiz.');
  }
  return clone(op);
}
export function makeRow(scope, workoutId, op, sequence, now = Date.now()) {
  if (typeof scope !== 'string' || !scope || scope.length > 1000 || !/^[a-zA-Z0-9_-]{1,128}$/.test(workoutId)) throw new Error('Kayıt sahibi veya antrenman geçersiz.');
  return { key: pk(scope, op.opId), scope, workoutId, op: validateOperation(op), sequence, createdAt: now, status: 'pending', attempts: 0, code: null, lease: null, leaseUntil: 0 };
}
export class IndexedOutbox {
  constructor({ indexedDB = globalThis.indexedDB, name = 'csm-member-private-v1', now = () => Date.now() } = {}) {
    if (!indexedDB) throw new Error('Bu tarayıcı kalıcı çevrimdışı kaydı desteklemiyor.');
    this.indexedDB = indexedDB; this.name = name; this.now = now; this.connection = null;
  }
  async open() {
    if (!this.connection) this.connection = new Promise((resolve, reject) => {
      const r = this.indexedDB.open(this.name, 1);
      r.onupgradeneeded = () => {
        const db = r.result;
        const ops = db.createObjectStore('ops', { keyPath: 'key' });
        ops.createIndex('scope', 'scope', { unique: false });
        db.createObjectStore('meta', { keyPath: 'key' });
      };
      r.onsuccess = () => { r.result.onversionchange = () => { r.result.close(); this.connection = null; }; resolve(r.result); };
      r.onerror = () => { this.connection = null; reject(r.error); };
      r.onblocked = () => { this.connection = null; reject(new Error('Kayıt deposu başka sekmede açık; sekmeleri yenileyin.')); };
    });
    return this.connection;
  }
  async tx(mode, callback) {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(['ops','meta'], mode); let result;
      tx.oncomplete = () => resolve(result);
      tx.onabort = () => reject(tx.error || new Error('Yerel kayıt tamamlanamadı.'));
      tx.onerror = () => {}; // onabort owns rejection; never report success early.
      try { callback(tx.objectStore('ops'), tx.objectStore('meta'), value => { result = value; }, tx); }
      catch (e) { tx.abort(); reject(e); }
    });
  }
  async list(scope) {
    return this.tx('readonly', (ops, _meta, done) => {
      const r = ops.index('scope').getAll(scope);
      r.onsuccess = () => done(r.result.sort((a,b) => a.sequence-b.sequence));
    });
  }
  async enqueue(scope, workoutId, op, canWrite = () => true) {
    validateOperation(op);
    return this.tx('readwrite', (ops, meta, done, tx) => {
      // Recheck inside the IndexedDB transaction, after open()/queued work can yield.
      if (!canWrite()) { done(null); return; }
      const key = pk(scope, op.opId), existing = ops.get(key);
      existing.onsuccess = () => {
        if (!canWrite()) { done(null); return; }
        if (existing.result) {
          if (existing.result.workoutId !== workoutId || JSON.stringify(existing.result.op) !== JSON.stringify(op)) { tx.abort(); return; }
          done(existing.result); return;
        }
        const counterKey = pk(scope, 'sequence'), count = meta.get(counterKey);
        count.onsuccess = () => {
          if (!canWrite()) { done(null); return; }
          const sequence = (count.result?.value || 0) + 1, row = makeRow(scope, workoutId, op, sequence, this.now());
          ops.add(row); meta.put({ key: counterKey, value: sequence }); done(row);
        };
      };
    });
  }
  async claim(scope, lease, leaseMs = 30000) {
    return this.tx('readwrite', (ops, _meta, done) => {
      const r = ops.index('scope').getAll(scope);
      r.onsuccess = () => {
        const rows = r.result.sort((a,b) => a.sequence-b.sequence), now = this.now();
        // An unresolved row or live claim blocks only its workout, not every other workout.
        const blocked = new Set(rows.filter(x => ['conflict','rejected'].includes(x.status) || (x.status === 'sending' && x.leaseUntil > now)).map(x => x.workoutId));
        const row = rows.find(x => !blocked.has(x.workoutId) && (x.status === 'pending' || (x.status === 'sending' && x.leaseUntil <= now)));
        if (!row) { done(null); return; }
        Object.assign(row, { status: 'sending', lease, leaseUntil: now + leaseMs, attempts: row.attempts + 1 });
        ops.put(row); done(row);
      };
    });
  }
  async settle(row, lease, status, code = null) {
    return this.tx('readwrite', (ops, _meta, done) => {
      const r = ops.get(row.key);
      r.onsuccess = () => {
        const current = r.result;
        if (!current || current.lease !== lease) { done(false); return; }
        if (status === 'ack') ops.delete(row.key);
        else ops.put({ ...current, status, code, lease: null, leaseUntil: 0 });
        done(true);
      };
    });
  }
  async discardWorkout(scope, workoutId) {
    return this.tx('readwrite', (ops, _meta, done) => {
      const r = ops.index('scope').getAll(scope);
      r.onsuccess = () => { const rows = r.result.filter(x => x.workoutId === workoutId); rows.forEach(x => ops.delete(x.key)); done(rows.length); };
    });
  }
  async remember(scope, key, value, canWrite = () => true) {
    if (!['activeWorkout', 'restDeadline', 'pendingOrder'].includes(key)) throw new Error('Bu veri yerel depoda tutulamaz.');
    return this.tx('readwrite', (_ops, meta, done) => { if (!canWrite()) { done(false); return; } meta.put({ key: pk(scope, key), value: clone(value) }); done(true); });
  }
  async recall(scope, key) {
    return this.tx('readonly', (_ops, meta, done) => { const r = meta.get(pk(scope,key)); r.onsuccess = () => done(r.result?.value ?? null); });
  }
  async purge(scope) {
    return this.tx('readwrite', (ops, meta, done) => {
      const r = ops.index('scope').getAll(scope);
      r.onsuccess = () => { r.result.forEach(x => ops.delete(x.key)); for (const k of ['sequence','activeWorkout','restDeadline','pendingOrder']) meta.delete(pk(scope,k)); done(true); };
    });
  }
  async close() { (await this.open()).close(); this.connection = null; }
}
/** Test double only. Actual IndexedDB behavior must be tested by browser_e2e.py. */
export class MemoryOutbox {
  constructor(now = () => Date.now()) { this.rows = new Map(); this.meta = new Map(); this.seq = new Map(); this.now = now; }
  async list(scope) { return [...this.rows.values()].filter(x => x.scope === scope).sort((a,b) => a.sequence-b.sequence).map(clone); }
  async enqueue(scope, workoutId, op, canWrite = () => true) {
    if (!canWrite()) return null;
    const key = pk(scope,op.opId), old = this.rows.get(key);
    if (old) { if (old.workoutId !== workoutId || JSON.stringify(old.op) !== JSON.stringify(op)) throw new Error('LOCAL_OP_CONFLICT'); return clone(old); }
    const seq = (this.seq.get(scope)||0)+1, row = makeRow(scope, workoutId, op, seq, this.now());
    this.seq.set(scope,seq); this.rows.set(key,row); return clone(row);
  }
  async claim(scope, lease, leaseMs = 30000) {
    const rows = [...this.rows.values()].filter(x => x.scope === scope).sort((a,b) => a.sequence-b.sequence), now = this.now();
    const blocked = new Set(rows.filter(x => ['conflict','rejected'].includes(x.status) || (x.status==='sending' && x.leaseUntil>now)).map(x=>x.workoutId));
    const row = rows.find(x => !blocked.has(x.workoutId) && (x.status==='pending' || (x.status==='sending' && x.leaseUntil<=now)));
    if (!row) return null;
    Object.assign(row,{status:'sending',lease,leaseUntil:now+leaseMs,attempts:row.attempts+1}); return clone(row);
  }
  async settle(row,lease,status,code=null) {
    const current = this.rows.get(row.key); if (!current || current.lease!==lease) return false;
    if (status==='ack') this.rows.delete(row.key); else Object.assign(current,{status,code,lease:null,leaseUntil:0}); return true;
  }
  async discardWorkout(scope,workoutId) { let n=0; for (const [k,r] of this.rows) if(r.scope===scope && r.workoutId===workoutId){this.rows.delete(k);n++;} return n; }
  async purge(scope) { for(const [k,r] of this.rows) if(r.scope===scope) this.rows.delete(k); for(const k of ['sequence','activeWorkout','restDeadline','pendingOrder'])this.meta.delete(pk(scope,k));this.seq.delete(scope); }
  async remember(scope,key,value,canWrite=()=>true){ if (!canWrite()) return false; this.meta.set(pk(scope,key),clone(value)); return true; }
  async recall(scope,key){ return clone(this.meta.get(pk(scope,key))??null); }
  async close() {}
}
export function validReceipt(row, receipt) {
  if (!receipt || !int(receipt.revision,1,1_000_000)) return false;
  if (row.op.type === 'finish') return receipt.status === 'finished';
  return ['saved','deleted'].includes(receipt.status) && int(receipt.setRevision,1,10001);
}
export class OutboxSync {
  constructor({ store, session, send, onChange = () => {}, onAck = () => {}, online = () => globalThis.navigator?.onLine !== false }) {
    this.store=store; this.session=session; this.send=send; this.onChange=onChange;this.onAck=onAck;this.online=online;this.running=null;
  }
  async flush() {
    if (this.running) return this.running;
    if (!this.session.scope || !this.online()) return;
    const ticket=this.session.capture(), lease=operationId('lease');
    this.running=(async()=>{
      while(this.session.valid(ticket) && this.online()) {
        const row=await this.store.claim(ticket.scope,lease);
        if(!row)break;
        if(!this.session.valid(ticket)){await this.store.settle(row,lease,'pending');break;}
        try {
          const receipt=await this.send(row,ticket);
          if(!this.session.valid(ticket)){await this.store.settle(row,lease,'pending');break;}
          if(!validReceipt(row,receipt))throw new ApiError('INVALID_RECEIPT','Kayıt teyidi geçersiz.',502,true);
          await this.store.settle(row,lease,'ack');
          if(this.session.valid(ticket))await this.onAck(row,receipt);
        }catch(e){
          if(!this.session.valid(ticket)){await this.store.settle(row,lease,'pending');break;}
          if(e.status===409){await this.store.settle(row,lease,'conflict',e.code);}
          else if(e.status>=400 && e.status<500 && ![401,403,408,429].includes(e.status)){await this.store.settle(row,lease,'rejected',e.code);}
          else {await this.store.settle(row,lease,'pending',e.code||'NETWORK');break;}
        }
        if(this.session.valid(ticket))await this.onChange();
      }
    })().finally(()=>{this.running=null; if(this.session.valid(ticket))this.onChange();});
    return this.running;
  }
}
